/**
 * The Apps tab's Traces for one service: from the service map of the last hour (`GET /traces/map`), who calls it and
 * whom it calls, with the calls and the failed ones. "Open Traces" opens the full tab on this service.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { getMap } from '../../../services/TracesService';
import { traceLinks } from '../../../utils/serviceTabsModel';
import { Card, LoadState, OpenLink, plural, useLoaded } from './common';

const RANGE = '1h';

function Links({ rows, empty, testId }) {
  if (rows.length === 0) { return <p className="apps-muted">{empty}</p>; }
  return (
    <ul className="apps-rows" data-testid={testId}>
      {rows.map((row) => (
        <li key={row.id}>
          <span className="apps-row-name">{row.peer}{row.operations.length > 0 && <span className="apps-muted apps-small"> {row.operations.slice(0, 2).join(', ')}</span>}</span>
          <span>{plural(row.calls, 'call')}{row.errors > 0 && <> · <strong className="apps-bad">{plural(row.errors, 'error')}</strong></>}</span>
        </li>
      ))}
    </ul>
  );
}
Links.propTypes = { empty: PropTypes.string, rows: PropTypes.array.isRequired, testId: PropTypes.string };

export default function TracesTab({ projectId, service, onOpen = null }) {
  const state = useLoaded(() => getMap(projectId, { since: RANGE }), [projectId]);
  const links = traceLinks(state.value, service);
  const off = state.value && state.value.enabled === false;
  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-traces">
      {state.loading || state.error || off || !links ? (
        <Card testId="apps-traces-none" title="Requests in the last hour">
          {off ? <p className="apps-muted">Traces are off{state.value.reason ? `: ${state.value.reason}` : '.'}</p>
            : <LoadState empty="No request reached this service in the last hour. Use the app in the Preview to see who calls it." state={state} />}
        </Card>
      ) : (
        <div className="apps-infos">
          <Card testId="apps-traces-in" title="Called by">
            <Links empty="Nothing called it in the last hour." rows={links.incoming} testId="apps-traces-in-list" />
          </Card>
          <Card testId="apps-traces-out" title="Calls">
            <Links empty="It called nothing in the last hour." rows={links.outgoing} testId="apps-traces-out-list" />
          </Card>
        </div>
      )}
      <OpenLink label="Open Traces" onOpen={onOpen} testId="apps-open-traces" />
    </div>
  );
}

TracesTab.propTypes = { onOpen: PropTypes.func, projectId: PropTypes.string, service: PropTypes.string.isRequired };
